import React from "react";
import { makeStyles } from "tss-react/mui";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { MOP_TYPES } from "../../../contants";
import { CLEANING_RATES } from "../../../computations/contants";
import styles from "./details.module.scss";

const useStyles = makeStyles()((theme) => ({
  highlightedBackground: {
    backgroundColor: theme.palette.background.details.highlight,
  },
  highlightedItem: {
    color: `${theme.palette.text.tableItems.highlight} !important`,
  },
  normalItem: {
    color: `${theme.palette.text.tableItems.main} !important`,
  },
}));

const formatTime = (timeInMinutes) => {
  const hours = Math.floor(timeInMinutes / 60);
  const minutes = timeInMinutes - hours * 60;
  return `${hours > 0 ? `${hours} hours ` : ""}${
    minutes > 0 ? `${minutes} mins` : ""
  }`;
};

function createData(category, stringMop, flatMop, iMop) {
  return { category, stringMop, flatMop, iMop };
}

function Details({
  mopType,
  computations,
  currency,
  surfaceUnit,
  totalSavings,
}) {
  const { t } = useTranslation(["common"]);
  const { classes, cx } = useStyles();

  const rows = [
    createData(
      t("Cleaning rate"),
      `${CLEANING_RATES[MOP_TYPES.STRING_MOP]} ${surfaceUnit}/hr`,
      `${CLEANING_RATES[MOP_TYPES.MICRO_FIBRE_FLAT_MOP]} ${surfaceUnit}/hr`,
      `${CLEANING_RATES[mopType]} ${surfaceUnit}/hr`
    ),
    createData(
      t("Cleaning / one time"),
      formatTime(computations.mopsCleaningTimes[MOP_TYPES.STRING_MOP]),
      formatTime(
        computations.mopsCleaningTimes[MOP_TYPES.MICRO_FIBRE_FLAT_MOP]
      ),
      formatTime(computations.mopsCleaningTimes[mopType])
    ),
    createData(
      t("Labor"),
      `${currency}${computations.mopsLabourCosts[MOP_TYPES.STRING_MOP]}`,
      `${currency}${
        computations.mopsLabourCosts[MOP_TYPES.MICRO_FIBRE_FLAT_MOP]
      }`,
      `${currency}${computations.mopsLabourCosts[mopType]}`
    ),
    createData(
      t("Cleaning / year"),
      formatTime(computations.mopsYearlyCleaningTimes[MOP_TYPES.STRING_MOP]),
      formatTime(
        computations.mopsYearlyCleaningTimes[MOP_TYPES.MICRO_FIBRE_FLAT_MOP]
      ),
      formatTime(computations.mopsYearlyCleaningTimes[mopType])
    ),
    createData(
      t("Labor"),
      `${currency}${computations.mopsYearlyLabourCosts[MOP_TYPES.STRING_MOP]}`,
      `${currency}${
        computations.mopsYearlyLabourCosts[MOP_TYPES.MICRO_FIBRE_FLAT_MOP]
      }`,
      `${currency}${computations.mopsYearlyLabourCosts[mopType]}`
    ),
    createData(
      t("Detergent"),
      `${currency}${
        computations.mopsYearlyChemicalCosts[MOP_TYPES.STRING_MOP]
      }`,
      `${currency}${
        computations.mopsYearlyChemicalCosts[MOP_TYPES.MICRO_FIBRE_FLAT_MOP]
      }`,
      `${currency}${computations.mopsYearlyChemicalCosts[mopType]}`
    ),
  ];

  return (
    <div className={styles.container}>
      <Typography
        variant="h5"
        sx={{ textTransform: "uppercase" }}
        color="text.main"
      >
        {t("Your yearly labor & detergent savings")}
      </Typography>
      <Typography
        variant="h2"
        sx={{ fontWeight: 900, marginBottom: "1rem" }}
        color="text.main"
      >
        <span className={styles.currency}>{currency}</span>
        {totalSavings}
      </Typography>
      <Box sx={{ pr: "1em" }}>
        <Table
          sx={{
            "& th, td": { color: "white", fontSize: "1em" },
            "& th": { fontWeight: "bold" },
          }}
          aria-label="details table"
        >
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell align="left" className={classes.normalItem}>
                {t(MOP_TYPES.STRING_MOP)}
              </TableCell>
              <TableCell align="left" className={classes.normalItem}>
                {t(MOP_TYPES.MICRO_FIBRE_FLAT_MOP)}
              </TableCell>
              <TableCell
                align="left"
                className={cx(
                  classes.highlightedItem,
                  classes.highlightedBackground
                )}
              >
                {t(mopType)}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  className={classes.normalItem}
                >
                  {row.category}
                </TableCell>
                <TableCell align="left" className={classes.normalItem}>
                  {row.stringMop}
                </TableCell>
                <TableCell align="left" className={classes.normalItem}>
                  {row.flatMop}
                </TableCell>
                <TableCell
                  align="left"
                  className={cx(
                    classes.highlightedItem,
                    classes.highlightedBackground
                  )}
                >
                  {row.iMop}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </div>
  );
}

export default Details;
